// Sample Request Types

export type SampleRequestStatus =
  | 'submitted'
  | 'cancelled'
  | 'pending_review'
  | 'accepted'
  | 'rejected'
  // FIR-7033: manual fulfillment stages, set from the sample table.
  | 'sent'
  | 'delivered'

// FIR-7033: statuses a standard/admin user may set manually, in promote order.
// Mirrors MANUAL_FULFILLMENT_STATUSES in server/samples/models.py.
export type ManualFulfillmentStatus = 'submitted' | 'sent' | 'delivered'

export const MANUAL_FULFILLMENT_STATUSES: ManualFulfillmentStatus[] = [
  'submitted',
  'sent',
  'delivered',
]

// FIR-7033: shipping carriers. Mirrors SampleCarrier in server/samples/models.py.
export type SampleCarrier = 'dot_foods' | 'ups' | 'fedex' | 'usps' | 'other'

export type FulfillmentOption =
  | 'pick_up_locally'
  | 'ship_to_address'
  | 'ship_with_existing_order'

export interface SampleRequestLineItem {
  id: number
  variant?: number // variant ID (present in detail serializer)
  variant_name: string
  variant_sku: string
  product_name: string
  quantity: number | null
  product_format: string | null
  // Note: variant_gtin removed per FIR-4061
}

export interface SampleRequestLineItemInput {
  variant_id: number
  quantity: number
  product_format?: string
}

export interface SampleRequest {
  id: number
  request_number: string
  status: SampleRequestStatus
  status_display: string
  // FIR-7033: manual fulfillment tracking
  tracking_number?: string
  tracking_carrier?: SampleCarrier | ''
  tracking_carrier_display?: string
  shipped_date?: string | null
  fulfillment_option_display: string
  date_needed: string
  created: string
  modified: string
  user_name: string
  user_email: string
  company_name: string
  company_id: number
  company_notification_link?: string
  // Location/door proxy fields (FIR-4061)
  location_id?: number | null
  location_name?: string
  location_address?: string
  location_notification_link?: string
  reviewed_by_name?: string
  reviewed_at?: string | null
  line_items_count: number
  line_items: SampleRequestLineItem[]
  ship_to_name?: string
  contact_email?: string
  contact_phone?: string
  shipping_address_line1?: string
  shipping_address_line2?: string
  shipping_city?: string
  shipping_state?: string
  shipping_zip?: string
  shipping_country?: string
}

export interface SampleRequestDetail {
  id: number
  request_number: string
  status: SampleRequestStatus
  status_display: string
  // FIR-7033: manual fulfillment tracking
  tracking_number?: string
  tracking_carrier?: SampleCarrier | ''
  tracking_carrier_display?: string
  shipped_date?: string | null
  user_name: string
  user_email: string
  company_name: string
  company_id: number
  distributor: number | null
  distributor_name: string | null
  date_needed: string
  fulfillment_option: FulfillmentOption
  fulfillment_option_display: string
  shipping_address_line1: string
  shipping_address_line2: string
  shipping_city: string
  shipping_state: string
  shipping_zip: string
  shipping_country: string
  contact_first_name: string
  contact_last_name: string
  contact_email: string | null
  contact_phone: string
  special_instructions: string
  line_items: SampleRequestLineItem[]
  created: string
  modified: string
  submitted_at: string | null
  cancelled_at: string | null
  cancellation_reason: string
  reviewed_by_name?: string
  reviewed_at?: string | null
  rejection_reason?: string
  can_review?: boolean
}

export interface SampleRequestCreateUpdate {
  distributor_id?: number | null
  contact_company_id?: number
  chain_id?: number | null
  door_proxy_id?: number | null
  contact_id?: number | null
  line_items: SampleRequestLineItemInput[]
  date_needed: string
  fulfillment_option: FulfillmentOption
  shipping_address_line1: string
  shipping_address_line2?: string
  shipping_city: string
  shipping_state: string
  shipping_zip: string
  shipping_country?: string
  contact_first_name: string
  contact_last_name: string
  contact_email?: string
  contact_phone: string
  special_instructions?: string
  status?: SampleRequestStatus
}

export interface SampleRequestsListResponse {
  results: SampleRequest[]
  count: number
  page: number
  page_size: number
  total_pages: number
}

export interface SampleRequestFilters {
  status?: SampleRequestStatus | SampleRequestStatus[] // Support single or multiple statuses
  status__exclude?: string[] // Exclude specific statuses
  fulfillment_option?: FulfillmentOption
  search?: string
  tracking_number?: string // FIR-7033: contains-match on tracking number
  requestor?: string[] // ID-based filter for FIR-4061
  product?: string[] // ID-based filter for FIR-4061
  contact_company_id?: number // Filter by contact company
  date_needed_after?: string
  date_needed_before?: string
  created_after?: string
  created_before?: string
  page?: number
  page_size?: number
  ordering?: string
  my_requests_only?: boolean
  /**
   * When true, scopes the list to pending_review samples whose requester
   * has the current user as their assigned RSM (Standard) or to all
   * pending_review samples in the company (Admin). Backend predicate
   * lives in server/samples/views; see FIR-6700.
   */
  needs_my_review?: boolean
}
